import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye, Pencil, Plus, Radio, Search } from "lucide-react";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AdminLiveWatch,
  LiveSessionStatus,
  LiveWatchKind,
  LiveWatchPlatform,
  useAdminCreateLiveWatchMutation,
  useAdminLiveWatchesQuery,
  useAdminSetLiveWatchEnabledMutation,
  useAdminUpdateLiveWatchMutation,
  useAdminYoutubeChannelsQuery,
} from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { displayLiveWatchUrl, parseLiveWatchUrl } from "@/lib/live-watch/parseLiveWatchUrl";
import { matchLibraryChannel } from "@/lib/live-watch/matchLibraryChannel";

interface LiveWatchFormData {
  platform: LiveWatchPlatform;
  kind: LiveWatchKind;
  platformChannelId: string;
  platformLogin: string;
  sourceUrl: string;
  youtubeChannelId: string;
  youtubeChannelName: string;
  enabled: boolean;
  windowMinutes: number;
  retentionDays: number;
  overlapSeconds: number;
}

const PAGE_SIZE = 20;

const initialFormData: LiveWatchFormData = {
  platform: LiveWatchPlatform.Youtube,
  kind: LiveWatchKind.Channel,
  platformChannelId: "",
  platformLogin: "",
  sourceUrl: "",
  youtubeChannelId: "",
  youtubeChannelName: "",
  enabled: true,
  windowMinutes: 10,
  retentionDays: 7,
  overlapSeconds: 30,
};

const statusVariant = (status?: LiveSessionStatus | null): "default" | "secondary" | "destructive" | "outline" => {
  if (status === LiveSessionStatus.Live) return "destructive";
  if (status === LiveSessionStatus.Failed) return "destructive";
  if (status === LiveSessionStatus.Pending) return "secondary";
  return "outline";
};

const AdminLiveWatches = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWatch, setSelectedWatch] = useState<AdminLiveWatch | null>(null);
  const [formData, setFormData] = useState<LiveWatchFormData>(initialFormData);
  const [debouncedChannelSearch, setDebouncedChannelSearch] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [liveUrlInput, setLiveUrlInput] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const channelLookup = parseLiveWatchUrl(liveUrlInput);
  const channelSearchTerm = (
    channelLookup?.platformChannelId || channelLookup?.platformLogin || ""
  ).trim();

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedChannelSearch(channelSearchTerm),
      300,
    );
    return () => window.clearTimeout(handle);
  }, [channelSearchTerm]);

  const { data, loading, error, refetch } = useAdminLiveWatchesQuery({
    variables: {
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
  });
  const { data: channelData, loading: channelsLoading } = useAdminYoutubeChannelsQuery({
    variables: {
      page: 1,
      pageSize: 100,
      search: debouncedChannelSearch || undefined,
    },
    skip: !dialogOpen || !debouncedChannelSearch,
  });
  const [createWatch, { loading: creating }] = useAdminCreateLiveWatchMutation();
  const [updateWatch, { loading: updating }] = useAdminUpdateLiveWatchMutation();
  const [setEnabled] = useAdminSetLiveWatchEnabledMutation();

  const watches = data?.adminLiveWatches.data ?? [];
  const total = data?.adminLiveWatches.total ?? 0;
  const totalPages = data?.adminLiveWatches.pageCount ?? 1;
  const channels = useMemo(
    () => channelData?.adminYoutubeChannels.data ?? [],
    [channelData],
  );

  useEffect(() => {
    if (!dialogOpen) return;
    if (!channelSearchTerm) {
      setFormData((current) => (
        current.youtubeChannelId
          ? { ...current, youtubeChannelId: "", youtubeChannelName: "" }
          : current
      ));
      return;
    }
    if (channelsLoading) return;
    const parsed = parseLiveWatchUrl(liveUrlInput);
    if (!parsed) return;
    const result = matchLibraryChannel({
      platformChannelId: parsed.platformChannelId,
      platformLogin: parsed.platformLogin,
      channels,
    });
    setFormData((current) => {
      if (result.status === "matched") {
        if (
          current.youtubeChannelId === result.channel.id
          && current.youtubeChannelName === result.channel.name
        ) {
          return current;
        }
        return {
          ...current,
          youtubeChannelId: result.channel.id,
          youtubeChannelName: result.channel.name,
        };
      }
      if (!current.youtubeChannelId) return current;
      return { ...current, youtubeChannelId: "", youtubeChannelName: "" };
    });
  }, [dialogOpen, channelSearchTerm, liveUrlInput, channels, channelsLoading]);

  const resetDialog = () => {
    setSelectedWatch(null);
    setFormData(initialFormData);
    setValidationError(null);
    setLiveUrlInput("");
  };

  const openCreateDialog = () => {
    resetDialog();
    setDialogOpen(true);
  };

  const openEditDialog = (watch: AdminLiveWatch) => {
    setSelectedWatch(watch);
    setFormData({
      platform: watch.platform,
      kind: watch.kind,
      platformChannelId: watch.platform_channel_id ?? "",
      platformLogin: watch.platform_login ?? "",
      sourceUrl: watch.source_url ?? "",
      youtubeChannelId: watch.youtube_channel?.id ?? "",
      youtubeChannelName: watch.youtube_channel?.name ?? "",
      enabled: watch.enabled,
      windowMinutes: watch.window_minutes,
      retentionDays: watch.retention_days,
      overlapSeconds: watch.overlap_seconds,
    });
    setValidationError(null);
    setLiveUrlInput(
      displayLiveWatchUrl({
        platform: watch.platform,
        kind: watch.kind,
        platformChannelId: watch.platform_channel_id ?? "",
        platformLogin: watch.platform_login ?? "",
        sourceUrl: watch.source_url ?? "",
      }),
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const parsed = parseLiveWatchUrl(liveUrlInput);
    const resolved = parsed
      ? {
          ...formData,
          platform: parsed.platform as LiveWatchPlatform,
          kind: parsed.kind as LiveWatchKind,
          platformChannelId: parsed.platformChannelId,
          platformLogin: parsed.platformLogin,
          sourceUrl: parsed.sourceUrl,
        }
      : formData;
    const resolvedIdentityIsValid =
      resolved.kind === LiveWatchKind.Url
        ? Boolean(resolved.sourceUrl.trim())
        : resolved.platform === LiveWatchPlatform.Youtube
          ? Boolean(resolved.platformChannelId.trim())
          : Boolean(resolved.platformLogin.trim());

    if (!resolvedIdentityIsValid) {
      setValidationError(t("admin.liveWatches.validation.identityRequired"));
      return;
    }

    const input = {
      platform: resolved.platform,
      kind: resolved.kind,
      platform_channel_id:
        resolved.kind === LiveWatchKind.Channel &&
        resolved.platform === LiveWatchPlatform.Youtube
          ? resolved.platformChannelId.trim()
          : null,
      platform_login:
        resolved.kind === LiveWatchKind.Channel &&
        resolved.platform !== LiveWatchPlatform.Youtube
          ? resolved.platformLogin.trim()
          : null,
      source_url:
        resolved.kind === LiveWatchKind.Url ? resolved.sourceUrl.trim() : null,
      youtube_channel_id: resolved.youtubeChannelId || undefined,
      enabled: formData.enabled,
      window_minutes: formData.windowMinutes,
      retention_days: formData.retentionDays,
      overlap_seconds: formData.overlapSeconds,
    };

    try {
      if (selectedWatch) {
        await updateWatch({ variables: { id: selectedWatch.id, input } });
      } else {
        await createWatch({ variables: { input } });
      }
      toast({
        title: t(
          selectedWatch
            ? "admin.liveWatches.toast.updated"
            : "admin.liveWatches.toast.created",
        ),
      });
      setDialogOpen(false);
      resetDialog();
      await refetch();
    } catch (saveError) {
      console.error("Failed to save live watch:", saveError);
      toast({
        title: t("admin.liveWatches.toast.saveError"),
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (watch: AdminLiveWatch, enabled: boolean) => {
    try {
      await setEnabled({ variables: { id: watch.id, enabled } });
      toast({ title: t("admin.liveWatches.toast.enabledUpdated") });
      await refetch();
    } catch (toggleError) {
      console.error("Failed to update live watch:", toggleError);
      toast({
        title: t("admin.liveWatches.toast.enabledError"),
        variant: "destructive",
      });
    }
  };

  const setFormField = <K extends keyof LiveWatchFormData>(
    field: K,
    value: LiveWatchFormData[K],
  ) => {
    setValidationError(null);
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleLiveUrlChange = (value: string) => {
    setLiveUrlInput(value);
    setValidationError(null);
    const parsed = parseLiveWatchUrl(value);
    if (!parsed) {
      setFormData((current) => ({
        ...current,
        youtubeChannelId: "",
        youtubeChannelName: "",
      }));
      return;
    }
    setFormData((current) => ({
      ...current,
      platform: parsed.platform as LiveWatchPlatform,
      kind: parsed.kind as LiveWatchKind,
      platformChannelId: parsed.platformChannelId,
      platformLogin: parsed.platformLogin,
      sourceUrl: parsed.sourceUrl,
      youtubeChannelId: "",
      youtubeChannelName: "",
    }));
  };

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Radio className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t("admin.liveWatches.title")}</h1>
                <p className="text-muted-foreground">{t("admin.liveWatches.subtitle")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-red-500 text-red-500">ADMIN</Badge>
              <Button onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                {t("admin.liveWatches.create")}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.liveWatches.list")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4 max-w-sm">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("admin.liveWatches.searchPlaceholder")}
                  className="pl-9"
                />
              </div>

              {loading && !data ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                </div>
              ) : error ? (
                <div className="py-8 text-center text-red-500">{t("admin.common.error")}</div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.liveWatches.fields.platform")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.kind")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.channel")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.enabled")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.currentStatus")}</TableHead>
                          <TableHead>{t("admin.liveWatches.fields.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {watches.map((watch) => (
                          <TableRow key={watch.id}>
                            <TableCell className="capitalize">{watch.platform}</TableCell>
                            <TableCell>{t(`admin.liveWatches.kinds.${watch.kind}`)}</TableCell>
                            <TableCell>{watch.youtube_channel?.name ?? "-"}</TableCell>
                            <TableCell>
                              <Switch
                                checked={watch.enabled}
                                onCheckedChange={(enabled) => handleToggleEnabled(watch, enabled)}
                                aria-label={t("admin.liveWatches.fields.enabled")}
                              />
                            </TableCell>
                            <TableCell>
                              {watch.current_session ? (
                                <div className="flex flex-wrap gap-1">
                                  <Badge variant={statusVariant(watch.current_session.status)}>
                                    {t(`admin.liveWatches.statuses.${watch.current_session.status}`)}
                                  </Badge>
                                  <Badge variant="outline">
                                    {t(`admin.liveWatches.ingestStatuses.${watch.current_session.ingest_status}`)}
                                  </Badge>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">
                                  {t("admin.liveWatches.statuses.none")}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button asChild variant="ghost" size="sm">
                                  <Link to={`/admin/live-watches/${watch.id}`}>
                                    <Eye className="h-4 w-4" />
                                  </Link>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(watch)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {watches.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                              {t("admin.liveWatches.empty")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetDialog();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t(selectedWatch ? "admin.liveWatches.edit" : "admin.liveWatches.create")}
            </DialogTitle>
            <DialogDescription>{t("admin.liveWatches.formDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="live-watch-live-url">{t("admin.liveWatches.fields.sourceUrl")}</Label>
              <Input
                id="live-watch-live-url"
                value={liveUrlInput}
                onChange={(event) => handleLiveUrlChange(event.target.value)}
                placeholder={t("admin.liveWatches.liveUrlPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("admin.liveWatches.fields.channel")}</Label>
              {channelsLoading && channelSearchTerm ? (
                <p className="text-sm text-muted-foreground">
                  {t("admin.liveWatches.channelMatching")}
                </p>
              ) : formData.youtubeChannelId ? (
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  {formData.youtubeChannelName}
                </div>
              ) : liveUrlInput.trim() ? (
                <p className="text-sm text-muted-foreground">
                  {t("admin.liveWatches.channelWillCreate", {
                    name:
                      channelLookup?.platformLogin
                      || channelLookup?.platformChannelId
                      || liveUrlInput.trim(),
                  })}
                </p>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="live-watch-window">{t("admin.liveWatches.fields.windowMinutes")}</Label>
                <Input
                  id="live-watch-window"
                  type="number"
                  min={1}
                  value={formData.windowMinutes}
                  onChange={(event) => setFormField("windowMinutes", Number(event.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="live-watch-retention">{t("admin.liveWatches.fields.retentionDays")}</Label>
                <Input
                  id="live-watch-retention"
                  type="number"
                  min={1}
                  value={formData.retentionDays}
                  onChange={(event) => setFormField("retentionDays", Number(event.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="live-watch-overlap">{t("admin.liveWatches.fields.overlapSeconds")}</Label>
                <Input
                  id="live-watch-overlap"
                  type="number"
                  min={0}
                  value={formData.overlapSeconds}
                  onChange={(event) => setFormField("overlapSeconds", Number(event.target.value))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="live-watch-enabled"
                checked={formData.enabled}
                onCheckedChange={(enabled) => setFormField("enabled", enabled)}
              />
              <Label htmlFor="live-watch-enabled">{t("admin.liveWatches.fields.enabled")}</Label>
            </div>
            {validationError && <p className="text-sm text-destructive">{validationError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={creating || updating}>
              {creating || updating ? t("admin.liveWatches.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminLiveWatches;
